import Select from "react-select";
import type { StylesConfig } from "react-select";
import { useTranslation } from "react-i18next";
import type { ThemeClasses } from "./types";
import { FilterPopover } from "../../../shared";
import { useDisableCRUD } from "../../../../../store/admin/companiesStore";
import { CRUDTooltip } from "../../../../../components/base/CRUDTooltip";

interface AvisosHeaderProps {
  isDarkMode: boolean;
  themeClasses: ThemeClasses;
  customSelectStyles: StylesConfig;
  selectedStatus: string;
  setSelectedStatus: (value: string) => void;
  selectedScope: string;
  setSelectedScope: (value: string) => void;
  search: string;
  setSearch: (value: string) => void;
  selectedView: "grid" | "list";
  setSelectedView: (value: "grid" | "list") => void;
  onAddClick: () => void;
  onSendToAllClick: () => void;
  isReseller: boolean;
}

export default function AvisosHeader({
  isDarkMode,
  themeClasses,
  customSelectStyles,
  selectedStatus,
  setSelectedStatus,
  selectedScope,
  setSelectedScope,
  search,
  setSearch,
  selectedView,
  setSelectedView,
  onAddClick,
  onSendToAllClick,
  isReseller,
}: AvisosHeaderProps) {
  const { t } = useTranslation("adminSections");

  // True when no company is selected in companies store. We use the same
  // signal as notifications: that's when "Nuevo aviso global" appears.
  const noCompanySelected = useDisableCRUD();

  const statusOptions = [
    { value: "all", label: t("avisos.allStatuses") },
    { value: "active", label: t("avisos.actives") },
    { value: "inactive", label: t("avisos.inactives") },
  ];

  const scopeOptions = [
    { value: "all", label: t("avisos.allScopes") },
    { value: "global", label: t("avisos.scopeGlobal") },
    { value: "company", label: t("avisos.scopeCompany") },
  ];

  return (
    <div className={`${themeClasses.bg} px-4 py-3 sm:px-6 sm:py-4 min-w-0`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:flex-wrap justify-between gap-3 sm:gap-4 mb-3">
        <div className="min-w-0 flex-1">
          <h1 className={`text-xl sm:text-2xl font-bold ${themeClasses.text}`}>
            {t("avisos.title")}
          </h1>
          <p className={`text-sm ${themeClasses.textSecondary} mt-1`}>
            {t("avisos.subtitle")}
          </p>
        </div>
        <div className="flex gap-2 self-start sm:self-auto flex-wrap">
          {isReseller && noCompanySelected && (
            <button
              onClick={onSendToAllClick}
              className={`${
                isDarkMode
                  ? "bg-blue-600 hover:bg-blue-700"
                  : "bg-blue-500 hover:bg-blue-600"
              } text-white px-3 sm:px-4 py-2 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors whitespace-nowrap text-sm sm:text-base`}
            >
              <i className="ri-global-line"></i>
              <span className="hidden lg:inline">
                {t("avisos.sendToAll")}
              </span>
              <span className="lg:hidden">{t("avisos.sendToAllShort")}</span>
            </button>
          )}
          <CRUDTooltip>
            <button
              onClick={onAddClick}
              disabled={noCompanySelected}
              className="bg-primary-500 hover:bg-primary-400 text-gray-900 px-3 sm:px-4 py-2 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors whitespace-nowrap text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="ri-add-line"></i>
              <span className="hidden sm:inline">
                {t("avisos.newAviso")}
              </span>
              <span className="sm:hidden">{t("avisos.newAvisoShort")}</span>
            </button>
          </CRUDTooltip>
        </div>
      </div>

      <div className="flex items-center gap-2 min-w-0">
        <div className="relative flex-1 min-w-0 sm:flex-none sm:w-72">
          <i
            className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary} text-sm`}
          ></i>
          <input
            type="text"
            placeholder={t("avisos.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`pl-9 pr-3 py-1.5 h-9 ${
              isDarkMode ? "bg-dark-card" : "bg-gray-50"
            } border ${themeClasses.cardBorder} rounded-lg ${
              themeClasses.text
            } placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm w-full`}
          />
        </div>

        <FilterPopover
          isDarkMode={isDarkMode}
          activeCount={
            (selectedStatus !== "all" ? 1 : 0) + (selectedScope !== "all" ? 1 : 0)
          }
          onClear={() => {
            setSelectedStatus("all");
            setSelectedScope("all");
          }}
        >
          <Select
            options={statusOptions}
            value={statusOptions.find((s) => s.value === selectedStatus)}
            onChange={(option) =>
              setSelectedStatus(((option as { value: string })?.value) || "all")
            }
            styles={customSelectStyles}
            isSearchable={false}
            placeholder={t("avisos.filterByStatus")}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />

          <Select
            options={scopeOptions}
            value={scopeOptions.find((s) => s.value === selectedScope)}
            onChange={(option) =>
              setSelectedScope(((option as { value: string })?.value) || "all")
            }
            styles={customSelectStyles}
            isSearchable={false}
            placeholder={t("avisos.filterByScope")}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </FilterPopover>

        <div
          className={`ml-auto flex shrink-0 ${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-lg p-1`}
        >
          <button
            className={`px-3 py-1 rounded-md transition-colors ${
              selectedView === "grid"
                ? "bg-primary-500 text-gray-900"
                : `${themeClasses.buttonText} ${themeClasses.buttonHover}`
            }`}
            onClick={() => setSelectedView("grid")}
            title="Vista cuadrícula"
          >
            <i className="ri-grid-fill"></i>
          </button>
          <button
            className={`px-3 py-1 rounded-md transition-colors ${
              selectedView === "list"
                ? "bg-primary-500 text-gray-900"
                : `${themeClasses.buttonText} ${themeClasses.buttonHover}`
            }`}
            onClick={() => setSelectedView("list")}
            title="Vista lista"
          >
            <i className="ri-list-check"></i>
          </button>
        </div>
      </div>
    </div>
  );
}
